import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Play, RefreshCw, Calendar, Monitor, Smartphone, Tablet, Clock, Activity, Globe, Video, Eye, ExternalLink, Tag, MapPin, AlertCircle, Filter, X, ChevronDown, Check } from 'lucide-react';
import { selectLogInUsername, SelectUserWebsiteURL  } from '../../common/authenticateDashboard/authSlice';
import { useSelector } from 'react-redux';
import 'rrweb-player/dist/style.css';



const SessionReplayDashboard = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [viewCounts, setViewCounts] = useState({});
  const [sessionEvents, setSessionEvents] = useState(null);
  const [sessionDetails, setSessionDetails] = useState(null);
  const playerRef = useRef(null);
  const replayerRef = useRef(null);
  const sessionUser = useSelector(selectLogInUsername);
  const user = sessionUser?.split("@")[0]?.replace(/-prod$/, '');
  console.log("----user---", user)
  const [stats, setStats] = useState({
    total: 0,
    events: 0,
    duration: '0m',
    pages: 0,
    emptySessions: 0
  });

  // Filter states
  const [filters, setFilters] = useState({
    referrer: 'all',
    utmSource: 'all',
    utmMedium: 'all',
    utmCampaign: 'all',
    hasGclid: 'all',
    hasFbclid: 'all',
    hasTtclid: 'all',
    pageContains: '',
    selectedPages: [], // Array of selected page URLs
    pageKeywords: [] // Array of keywords to search in page URLs/titles
  });

  // Available filter options
  const [filterOptions, setFilterOptions] = useState({
    referrers: [],
    utmSources: [],
    utmMediums: [],
    utmCampaigns: [],
    pages: [] // Array of all unique pages
  });

  // UI state for page selector
  const [showPageSelector, setShowPageSelector] = useState(false);
  
  // UI state for keyword selector
  const [showKeywordSelector, setShowKeywordSelector] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  
  // UI state for showing empty sessions
  const [showEmptySessions, setShowEmptySessions] = useState(false);

  const API_ENDPOINT = 'https://clients.causalfunnels.com/api';
const DEFAULT_USER = user ;

  // Helper function to extract UTM parameters and tracking data from URL
  const extractTrackingFromUrl = (url) => {
    if (!url || typeof url !== 'string') return {};
    
    try {
      const urlObj = new URL(url);
      const params = urlObj.searchParams;
      
      return {
        utm_source: params.get('utm_source') || undefined,
        utm_medium: params.get('utm_medium') || undefined,
        utm_campaign: params.get('utm_campaign') || undefined,
        utm_term: params.get('utm_term') || undefined,
        utm_content: params.get('utm_content') || undefined,
        gclid: params.get('gclid') || undefined,
        fbclid: params.get('fbclid') || undefined,
        ttclid: params.get('ttclid') || undefined,
      };
    } catch (e) {
      console.warn('Failed to parse URL for tracking params:', url, e);
      return {};
    }
  };

  // Helper function to merge tracking data from multiple sources
  const mergeTrackingData = (trackingObj, urlTracking) => {
    const merged = { ...trackingObj };
    
    // Only use URL params if the tracking object has 'N/A' or is missing
    Object.keys(urlTracking).forEach(key => {
      if (urlTracking[key] && (!merged[key] || merged[key] === 'N/A')) {
        merged[key] = urlTracking[key];
      }
    });
    
    return merged;
  };

  // Load sessions from API
  const loadSessions = useCallback(async () => {
    setLoading(true);
    
    try {
      const url = `${API_ENDPOINT}/sessions/${DEFAULT_USER}${selectedDate ? `?date=${selectedDate}` : ''}`;
      console.log('Fetching from:', url);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Response:', data);
      
      if (data.success && data.sessions && data.sessions.length > 0) {
        // Show all sessions including those with 0 events
        setSessions(data.sessions);
        updateStats(data.sessions);
        extractFilterOptions(data.sessions);
        
        // Fetch page details for all sessions in parallel
        console.log('Fetching page details for all sessions...');
        const sessionsWithPages = await Promise.all(
          data.sessions.map(async (session) => {
            try {
              const details = await fetchSessionDetails(session.sessionId);
              
              if (details && details.success && details.documents) {
                // Extract pages with timestamps from documents
                const pagesWithTimestamp = [];
                details.documents.forEach(doc => {
                  const pageUrl = doc.data?.pageInfo?.url;
                  const pageTitle = doc.data?.pageInfo?.title;
                  const timestamp = doc.data?.pageInfo?.timestamp;
                  const sessionStartTime = doc.data?.sessionStartTime;
                  
                  if (pageUrl && timestamp) {
                    pagesWithTimestamp.push({
                      url: pageUrl,
                      title: pageTitle || pageUrl,
                      timestamp: timestamp,
                      sessionStartTime: sessionStartTime
                    });
                  }
                });
                
                // Sort by timestamp and get unique pages
                pagesWithTimestamp.sort((a, b) => a.timestamp - b.timestamp);
                
                // Get unique pages (keeping the first occurrence of each URL)
                const uniquePagesMap = new Map();
                pagesWithTimestamp.forEach(page => {
                  if (!uniquePagesMap.has(page.url)) {
                    uniquePagesMap.set(page.url, page);
                  }
                });
                const uniquePages = Array.from(uniquePagesMap.values());
                
                // Extract referrer from the earliest document (first in sorted order)
                let sessionReferrer = 'Direct';
                let enhancedTracking = { ...session.tracking };
                
                if (details.documents.length > 0) {
                  // Sort documents by timestamp to find the earliest
                  const sortedDocs = [...details.documents].sort((a, b) => {
                    const timeA = a.data?.pageInfo?.timestamp || 0;
                    const timeB = b.data?.pageInfo?.timestamp || 0;
                    return timeA - timeB;
                  });
                  
                  // Get referrer from the earliest document
                  const earliestDoc = sortedDocs[0];
                  const referrerFromPageInfo = earliestDoc.data?.pageInfo?.referrer;
                  const referrerFromTracking = earliestDoc.tracking?.referrer;
                  
                  // Use pageInfo referrer first, then tracking referrer
                  const extractedReferrer = referrerFromPageInfo || referrerFromTracking;
                  
                  // Only set if it's not "N/A" or empty
                  if (extractedReferrer && extractedReferrer !== 'N/A' && extractedReferrer.trim() !== '') {
                    sessionReferrer = extractedReferrer;
                  }
                  
                  // Extract tracking data from entry URL if tracking has N/A values
                  const entryUrl = uniquePages[0]?.url; // First page is the entry URL
                  if (entryUrl) {
                    const urlTracking = extractTrackingFromUrl(entryUrl);
                    enhancedTracking = mergeTrackingData(
                      earliestDoc.tracking || session.tracking || {},
                      urlTracking
                    );
                    console.log(`Session ${session.sessionId} - Enhanced tracking:`, {
                      original: session.tracking,
                      fromUrl: urlTracking,
                      enhanced: enhancedTracking
                    });
                  }
                }
                
                return {
                  ...session,
                  pages: uniquePages,
                  apiReferrer: sessionReferrer, // Store the referrer from earliest timestamp
                  enhancedTracking: enhancedTracking // Store enhanced tracking with URL params
                };
              }
              
              // Return session without pages if API failed
              return session;
            } catch (err) {
              console.warn(`Failed to fetch details for session ${session.sessionId}:`, err);
              return session;
            }
          })
        );
        
        console.log('Sessions with pages loaded:', sessionsWithPages);
        setSessions(sessionsWithPages);
        updateStats(sessionsWithPages);
        extractFilterOptions(sessionsWithPages);
      } else {
        setSessions([]);
        resetStats();
        setFilterOptions({
          referrers: [],
          utmSources: [],
          utmMediums: [],
          utmCampaigns: []
        });
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
      setSessions([]);
      resetStats();
      alert(`Failed to load sessions: ${error.message}\n\nUser: ${DEFAULT_USER}\nAPI: ${API_ENDPOINT}\n\nMake sure:\n- API server is running\n- MongoDB is connected\n- Collection '${DEFAULT_USER}-prod' exists`);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  // Update statistics
  const updateStats = (sessionsData) => {
    // Count empty sessions
    const emptySessions = sessionsData.filter(s => s.eventCount === 0).length;
    
    // Filter out sessions with 0 events
    const validSessions = sessionsData.filter(s => s.eventCount > 0);
    const totalSessions = validSessions.length;
    const totalEvents = validSessions.reduce((sum, s) => sum + s.eventCount, 0);
    const totalDuration = validSessions.reduce((sum, s) => {
      let start = s.startTime;
      let end = s.lastUpdate;
      if (typeof start === 'string') start = new Date(start).getTime();
      if (typeof end === 'string') end = new Date(end).getTime();
      return sum + Math.abs(end - start);
    }, 0);
    const avgDuration = totalSessions > 0 ? totalDuration / totalSessions : 0;
    
    const uniquePages = new Set();
    validSessions.forEach(s => {
      if (s.pages && Array.isArray(s.pages)) {
        s.pages.forEach(p => {
          // Add URL if it exists (check multiple possible fields)
          const url = p.url || p.pageUrl || p.href;
          if (url) {
            uniquePages.add(url);
          }
        });
      }
      // Also check pageInfo if it exists
      if (s.pageInfo?.url) {
        uniquePages.add(s.pageInfo.url);
      }
    });
    
    console.log('Unique pages count:', uniquePages.size, 'Pages:', Array.from(uniquePages));
    
    setStats({
      total: totalSessions,
      events: totalEvents,
      duration: formatDuration(avgDuration),
      pages: uniquePages.size,
      emptySessions: emptySessions
    });
  };

  // Reset stats
  const resetStats = () => {
    setStats({
      total: 0,
      events: 0,
      duration: '0m',
      pages: 0,
      emptySessions: 0
    });
  };

  // Extract unique filter options from sessions
  const extractFilterOptions = (sessionsData) => {
    const referrers = new Set();
    const utmSources = new Set();
    const utmMediums = new Set();
    const utmCampaigns = new Set();
    const pagesMap = new Map(); // Map to store URL -> title

    sessionsData.forEach(session => {
      // Use enhanced tracking if available, otherwise fallback
      const tracking = session.enhancedTracking || session.tracking || session.data?.tracking || {};
      
      // Use apiReferrer (from earliest timestamp) if available, otherwise fallback
      const referrer = session.apiReferrer || tracking.referrer || session.pageInfo?.referrer || session.pages?.[0]?.referrer;
      
      if (referrer && referrer !== 'Direct') {
        referrers.add(referrer);
      }
      
      if (tracking.utm_source && tracking.utm_source !== 'N/A') {
        utmSources.add(tracking.utm_source);
      }
      
      if (tracking.utm_medium && tracking.utm_medium !== 'N/A') {
        utmMediums.add(tracking.utm_medium);
      }
      
      if (tracking.utm_campaign && tracking.utm_campaign !== 'N/A') {
        utmCampaigns.add(tracking.utm_campaign);
      }

      // Extract unique pages
      if (session.pages && Array.isArray(session.pages)) {
        session.pages.forEach(page => {
          const url = page.url || page.pageUrl || page.href;
          if (url) {
            // Store the first title we find for this URL
            if (!pagesMap.has(url)) {
              pagesMap.set(url, page.title || url);
            }
          }
        });
      }
    });

    // Convert pages map to array of objects
    const pagesArray = Array.from(pagesMap.entries()).map(([url, title]) => ({
      url,
      title,
      displayText: title && title !== url ? `${title} (${url})` : url
    })).sort((a, b) => a.displayText.localeCompare(b.displayText));

    setFilterOptions({
      referrers: Array.from(referrers).sort(),
      utmSources: Array.from(utmSources).sort(),
      utmMediums: Array.from(utmMediums).sort(),
      utmCampaigns: Array.from(utmCampaigns).sort(),
      pages: pagesArray
    });
  };

  // Filter sessions based on selected filters
  const getFilteredSessions = () => {
    return sessions.filter(session => {
      // Only show sessions with events (unless showEmptySessions is true)
      if (session.eventCount === 0 && !showEmptySessions) return false;

      // Use enhanced tracking if available, otherwise fallback
      const tracking = session.enhancedTracking || session.tracking || session.data?.tracking || {};
      
      // Use apiReferrer (from earliest timestamp) if available, otherwise fallback
      const referrer = session.apiReferrer || tracking.referrer || session.pageInfo?.referrer || session.pages?.[0]?.referrer || 'Direct';
      
      // Filter by referrer
      if (filters.referrer !== 'all') {
        if (filters.referrer === 'direct' && referrer !== 'Direct') return false;
        if (filters.referrer !== 'direct' && referrer !== filters.referrer) return false;
      }
      
      // Filter by UTM source
      if (filters.utmSource !== 'all') {
        if (tracking.utm_source !== filters.utmSource) return false;
      }
      
      // Filter by UTM medium
      if (filters.utmMedium !== 'all') {
        if (tracking.utm_medium !== filters.utmMedium) return false;
      }
      
      // Filter by UTM campaign
      if (filters.utmCampaign !== 'all') {
        if (tracking.utm_campaign !== filters.utmCampaign) return false;
      }
      
      // Filter by Google Click ID (gclid)
      if (filters.hasGclid !== 'all') {
        const hasGclid = tracking.gclid && tracking.gclid !== 'N/A';
        if (filters.hasGclid === 'yes' && !hasGclid) return false;
        if (filters.hasGclid === 'no' && hasGclid) return false;
      }
      
      // Filter by Facebook Click ID (fbclid)
      if (filters.hasFbclid !== 'all') {
        const hasFbclid = tracking.fbclid && tracking.fbclid !== 'N/A';
        if (filters.hasFbclid === 'yes' && !hasFbclid) return false;
        if (filters.hasFbclid === 'no' && hasFbclid) return false;
      }
      
      // Filter by TikTok Click ID (ttclid)
      if (filters.hasTtclid !== 'all') {
        const hasTtclid = tracking.ttclid && tracking.ttclid !== 'N/A';
        if (filters.hasTtclid === 'yes' && !hasTtclid) return false;
        if (filters.hasTtclid === 'no' && hasTtclid) return false;
      }
      
      // Filter by selected pages (multiple selection)
      if (filters.selectedPages.length > 0) {
        const hasMatchingPage = session.pages?.some(page => {
          const url = page.url || page.pageUrl || page.href;
          return url && filters.selectedPages.includes(url);
        });
        if (!hasMatchingPage) return false;
      }
      
      // Filter by page contains (text search)
      if (filters.pageContains.trim()) {
        const searchTerm = filters.pageContains.toLowerCase();
        const hasMatchingPage = session.pages?.some(page => 
          page.url?.toLowerCase().includes(searchTerm) || 
          page.title?.toLowerCase().includes(searchTerm)
        );
        if (!hasMatchingPage) return false;
      }
      
      // Filter by page keywords (multiple keywords - ALL must match)
      if (filters.pageKeywords.length > 0) {
        const allKeywordsMatch = filters.pageKeywords.every(keyword => {
          const keywordLower = keyword.toLowerCase();
          return session.pages?.some(page => 
            page.url?.toLowerCase().includes(keywordLower) || 
            page.title?.toLowerCase().includes(keywordLower)
          );
        });
        if (!allKeywordsMatch) return false;
      }
      
      return true;
    });
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      referrer: 'all',
      utmSource: 'all',
      utmMedium: 'all',
      utmCampaign: 'all',
      hasGclid: 'all',
      hasFbclid: 'all',
      hasTtclid: 'all',
      pageContains: '',
      selectedPages: [],
      pageKeywords: []
    });
    setShowPageSelector(false);
    setShowKeywordSelector(false);
    setKeywordInput('');
  };

  // Toggle page selection
  const togglePageSelection = (pageUrl) => {
    setFilters(prev => ({
      ...prev,
      selectedPages: prev.selectedPages.includes(pageUrl)
        ? prev.selectedPages.filter(url => url !== pageUrl)
        : [...prev.selectedPages, pageUrl]
    }));
  };

  // Select all pages
  const selectAllPages = () => {
    setFilters(prev => ({
      ...prev,
      selectedPages: filterOptions.pages.map(p => p.url)
    }));
  };

  // Deselect all pages
  const deselectAllPages = () => {
    setFilters(prev => ({
      ...prev,
      selectedPages: []
    }));
  };

  // Add keyword to filter
  const addKeyword = () => {
    const trimmedKeyword = keywordInput.trim();
    if (trimmedKeyword && !filters.pageKeywords.includes(trimmedKeyword)) {
      setFilters(prev => ({
        ...prev,
        pageKeywords: [...prev.pageKeywords, trimmedKeyword]
      }));
      setKeywordInput('');
    }
  };

  // Remove keyword from filter
  const removeKeyword = (keyword) => {
    setFilters(prev => ({
      ...prev,
      pageKeywords: prev.pageKeywords.filter(k => k !== keyword)
    }));
  };

  // Clear all keywords
  const clearAllKeywords = () => {
    setFilters(prev => ({
      ...prev,
      pageKeywords: []
    }));
  };

  // Extract userInfo from any document in the same session or from sessionDetails
  const getUserInfo = (session, details = null) => {
    // Check multiple possible locations for userInfo
    let userInfo = {
      userAgent: 'N/A',
      language: 'N/A',
      screenResolution: 'N/A',
      viewportSize: 'N/A',
      timezone: 'N/A',
      platform: 'N/A'
    };

    // Priority 0: Check tracking object directly (NEW STRUCTURE)
    if (session.tracking) {
      userInfo.userAgent = session.tracking.userAgent || session.tracking.user_agent || userInfo.userAgent;
      userInfo.language = session.tracking.language || userInfo.language;
      userInfo.screenResolution = session.tracking.screenResolution || userInfo.screenResolution;
      userInfo.viewportSize = session.tracking.viewportSize || userInfo.viewportSize;
      userInfo.timezone = session.tracking.timezone || userInfo.timezone;
      userInfo.platform = session.tracking.platform || userInfo.platform;
    }

    // Priority 1: Use sessionDetails if available (from API)
    if (details && details.userInfo) {
      userInfo.userAgent = userInfo.userAgent === 'N/A' ? (details.userInfo.userAgent || userInfo.userAgent) : userInfo.userAgent;
      userInfo.language = userInfo.language === 'N/A' ? (details.userInfo.language || userInfo.language) : userInfo.language;
      userInfo.screenResolution = userInfo.screenResolution === 'N/A' ? (details.userInfo.screenResolution || userInfo.screenResolution) : userInfo.screenResolution;
      userInfo.viewportSize = userInfo.viewportSize === 'N/A' ? (details.userInfo.viewportSize || userInfo.viewportSize) : userInfo.viewportSize;
      userInfo.timezone = userInfo.timezone === 'N/A' ? (details.userInfo.timezone || userInfo.timezone) : userInfo.timezone;
      userInfo.platform = userInfo.platform === 'N/A' ? (details.userInfo.platform || userInfo.platform) : userInfo.platform;
    }

    // Priority 2: Check tracking in sessionDetails (from API)
    if (details && details.tracking) {
      userInfo.userAgent = userInfo.userAgent === 'N/A' ? (details.tracking.userAgent || details.tracking.user_agent || userInfo.userAgent) : userInfo.userAgent;
      userInfo.language = userInfo.language === 'N/A' ? (details.tracking.language || userInfo.language) : userInfo.language;
      userInfo.screenResolution = userInfo.screenResolution === 'N/A' ? (details.tracking.screenResolution || userInfo.screenResolution) : userInfo.screenResolution;
      userInfo.viewportSize = userInfo.viewportSize === 'N/A' ? (details.tracking.viewportSize || userInfo.viewportSize) : userInfo.viewportSize;
      userInfo.timezone = userInfo.timezone === 'N/A' ? (details.tracking.timezone || userInfo.timezone) : userInfo.timezone;
      userInfo.platform = userInfo.platform === 'N/A' ? (details.tracking.platform || userInfo.platform) : userInfo.platform;
    }

    // Priority 3: Direct userInfo field
    if (session.userInfo) {
      userInfo.userAgent = userInfo.userAgent === 'N/A' ? (session.userInfo.userAgent || session.userInfo.user_agent || userInfo.userAgent) : userInfo.userAgent;
      userInfo.language = userInfo.language === 'N/A' ? (session.userInfo.language || session.userInfo.lang || userInfo.language) : userInfo.language;
      userInfo.screenResolution = userInfo.screenResolution === 'N/A' ? (session.userInfo.screenResolution || session.userInfo.screen || userInfo.screenResolution) : userInfo.screenResolution;
      userInfo.viewportSize = userInfo.viewportSize === 'N/A' ? (session.userInfo.viewportSize || session.userInfo.viewport || userInfo.viewportSize) : userInfo.viewportSize;
      userInfo.timezone = userInfo.timezone === 'N/A' ? (session.userInfo.timezone || session.userInfo.timeZone || userInfo.timezone) : userInfo.timezone;
      userInfo.platform = userInfo.platform === 'N/A' ? (session.userInfo.platform || userInfo.platform) : userInfo.platform;
    }

    // Priority 4: Check in pageInfo
    if (session.pageInfo) {
      userInfo.userAgent = userInfo.userAgent === 'N/A' ? (session.pageInfo.userAgent || session.pageInfo.user_agent || userInfo.userAgent) : userInfo.userAgent;
      userInfo.language = userInfo.language === 'N/A' ? (session.pageInfo.language || userInfo.language) : userInfo.language;
      userInfo.screenResolution = userInfo.screenResolution === 'N/A' ? (session.pageInfo.screenResolution || userInfo.screenResolution) : userInfo.screenResolution;
      userInfo.viewportSize = userInfo.viewportSize === 'N/A' ? (session.pageInfo.viewportSize || userInfo.viewportSize) : userInfo.viewportSize;
      userInfo.timezone = userInfo.timezone === 'N/A' ? (session.pageInfo.timezone || userInfo.timezone) : userInfo.timezone;
      userInfo.platform = userInfo.platform === 'N/A' ? (session.pageInfo.platform || userInfo.platform) : userInfo.platform;
    }

    // Priority 5: Check in pages array (first page with userInfo)
    if (session.pages && session.pages.length > 0) {
      for (const page of session.pages) {
        if (page.userInfo) {
          userInfo.userAgent = userInfo.userAgent === 'N/A' ? (page.userInfo.userAgent || page.userInfo.user_agent || userInfo.userAgent) : userInfo.userAgent;
          userInfo.language = userInfo.language === 'N/A' ? (page.userInfo.language || userInfo.language) : userInfo.language;
          userInfo.screenResolution = userInfo.screenResolution === 'N/A' ? (page.userInfo.screenResolution || userInfo.screenResolution) : userInfo.screenResolution;
          userInfo.viewportSize = userInfo.viewportSize === 'N/A' ? (page.userInfo.viewportSize || userInfo.viewportSize) : userInfo.viewportSize;
          userInfo.timezone = userInfo.timezone === 'N/A' ? (page.userInfo.timezone || userInfo.timezone) : userInfo.timezone;
          userInfo.platform = userInfo.platform === 'N/A' ? (page.userInfo.platform || userInfo.platform) : userInfo.platform;
        }
        
        // Also check direct fields on page
        userInfo.userAgent = userInfo.userAgent === 'N/A' ? (page.userAgent || page.user_agent || userInfo.userAgent) : userInfo.userAgent;
        userInfo.language = userInfo.language === 'N/A' ? (page.language || userInfo.language) : userInfo.language;
        userInfo.screenResolution = userInfo.screenResolution === 'N/A' ? (page.screenResolution || userInfo.screenResolution) : userInfo.screenResolution;
        userInfo.viewportSize = userInfo.viewportSize === 'N/A' ? (page.viewportSize || userInfo.viewportSize) : userInfo.viewportSize;
        userInfo.timezone = userInfo.timezone === 'N/A' ? (page.timezone || userInfo.timezone) : userInfo.timezone;
        userInfo.platform = userInfo.platform === 'N/A' ? (page.platform || userInfo.platform) : userInfo.platform;

        // Break if we have all data
        if (Object.values(userInfo).every(v => v !== 'N/A')) break;
      }
    }

    // Priority 6: Check in data object
    if (session.data) {
      userInfo.userAgent = userInfo.userAgent === 'N/A' ? (session.data.userAgent || session.data.user_agent || userInfo.userAgent) : userInfo.userAgent;
      userInfo.language = userInfo.language === 'N/A' ? (session.data.language || userInfo.language) : userInfo.language;
      userInfo.screenResolution = userInfo.screenResolution === 'N/A' ? (session.data.screenResolution || userInfo.screenResolution) : userInfo.screenResolution;
      userInfo.viewportSize = userInfo.viewportSize === 'N/A' ? (session.data.viewportSize || userInfo.viewportSize) : userInfo.viewportSize;
      userInfo.timezone = userInfo.timezone === 'N/A' ? (session.data.timezone || userInfo.timezone) : userInfo.timezone;
      userInfo.platform = userInfo.platform === 'N/A' ? (session.data.platform || userInfo.platform) : userInfo.platform;

      // Check nested userInfo in data
      if (session.data.userInfo) {
        userInfo.userAgent = userInfo.userAgent === 'N/A' ? (session.data.userInfo.userAgent || userInfo.userAgent) : userInfo.userAgent;
        userInfo.language = userInfo.language === 'N/A' ? (session.data.userInfo.language || userInfo.language) : userInfo.language;
        userInfo.screenResolution = userInfo.screenResolution === 'N/A' ? (session.data.userInfo.screenResolution || userInfo.screenResolution) : userInfo.screenResolution;
        userInfo.viewportSize = userInfo.viewportSize === 'N/A' ? (session.data.userInfo.viewportSize || userInfo.viewportSize) : userInfo.viewportSize;
        userInfo.timezone = userInfo.timezone === 'N/A' ? (session.data.userInfo.timezone || userInfo.timezone) : userInfo.timezone;
        userInfo.platform = userInfo.platform === 'N/A' ? (session.data.userInfo.platform || userInfo.platform) : userInfo.platform;
      }
    }

    return userInfo;
  };

  // Extract tracking data from session or sessionDetails
  const getTrackingData = (session, details = null) => {
    let trackingData = {};
    
    // Priority 0: Use enhanced tracking from session (already has URL params merged)
    if (session.enhancedTracking) {
      trackingData = { ...session.enhancedTracking };
    }
    // Priority 1: Use sessionDetails if available (from API)
    else if (details && details.tracking) {
      trackingData = { ...details.tracking };
    }
    // Priority 2: Fallback to session data
    else {
      trackingData = {
        utm_source: session.tracking?.utm_source || session.data?.tracking?.utm_source,
        utm_medium: session.tracking?.utm_medium || session.data?.tracking?.utm_medium,
        utm_campaign: session.tracking?.utm_campaign || session.data?.tracking?.utm_campaign,
        utm_term: session.tracking?.utm_term || session.data?.tracking?.utm_term,
        utm_content: session.tracking?.utm_content || session.data?.tracking?.utm_content,
        referrer: session.tracking?.referrer || session.data?.tracking?.referrer || session.pageInfo?.referrer || session.pages?.[0]?.referrer,
        gclid: session.tracking?.gclid || session.data?.tracking?.gclid,
        fbclid: session.tracking?.fbclid || session.data?.tracking?.fbclid,
        ttclid: session.tracking?.ttclid || session.data?.tracking?.ttclid,
      };
    }
    
    // If still N/A, try extracting from entry URL
    const entryUrl = session.pages?.[0]?.url;
    if (entryUrl && Object.values(trackingData).some(v => !v || v === 'N/A')) {
      const urlTracking = extractTrackingFromUrl(entryUrl);
      trackingData = mergeTrackingData(trackingData, urlTracking);
    }
    
    // Set default values for missing fields
    return {
      utm_source: trackingData.utm_source || 'N/A',
      utm_medium: trackingData.utm_medium || 'N/A',
      utm_campaign: trackingData.utm_campaign || 'N/A',
      utm_term: trackingData.utm_term || 'N/A',
      utm_content: trackingData.utm_content || 'N/A',
      referrer: trackingData.referrer || 'Direct',
      gclid: trackingData.gclid || 'N/A',
      fbclid: trackingData.fbclid || 'N/A',
      ttclid: trackingData.ttclid || 'N/A',
    };
  };

  // Detect device type from user agent
  const detectDeviceType = (userInfoObj) => {
    // Get user agent - check multiple possible fields
    const ua = (
      userInfoObj?.userAgent || 
      userInfoObj?.ua || 
      userInfoObj?.browser?.userAgent || 
      userInfoObj?.user_agent ||
      ''
    ).toLowerCase();
    
    // Get viewport/screen size - check multiple possible fields
    const viewport = 
      userInfoObj?.viewportSize || 
      userInfoObj?.viewport || 
      userInfoObj?.screenSize || 
      userInfoObj?.screen || 
      '';
    
    const [width] = viewport.toString().split('x').map(v => parseInt(v) || 0);

    console.log('Device detection:', { ua, viewport, width, userInfo: userInfoObj });

    // Mobile detection (more comprehensive)
    if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini|phone/i.test(ua)) {
      return { type: 'mobile', Icon: Smartphone, name: 'Mobile' };
    }
    
    // Tablet detection (more comprehensive)
    if (/tablet|ipad|playbook|silk|kindle/i.test(ua)) {
      return { type: 'tablet', Icon: Tablet, name: 'Tablet' };
    }
    
    // Width-based detection
    if (width > 0) {
      if (width < 768) {
        return { type: 'mobile', Icon: Smartphone, name: 'Mobile' };
      }
      if (width < 1024) {
        return { type: 'tablet', Icon: Tablet, name: 'Tablet' };
      }
    }

    // Default to desktop
    return { type: 'desktop', Icon: Monitor, name: 'Desktop' };
  };

  // Get readable device info from user agent
  const getDeviceInfo = (userAgent) => {
    if (!userAgent) return 'Unknown';
    
    const ua = userAgent.toLowerCase();
    
    // Operating System
    let os = 'Unknown OS';
    if (ua.includes('windows')) os = 'Windows';
    else if (ua.includes('mac os x')) os = 'macOS';
    else if (ua.includes('linux')) os = 'Linux';
    else if (ua.includes('android')) os = 'Android';
    else if (ua.includes('iphone') || ua.includes('ipad')) os = 'iOS';
    
    // Browser
    let browser = 'Unknown Browser';
    if (ua.includes('edg/')) browser = 'Edge';
    else if (ua.includes('chrome/') && !ua.includes('edg/')) browser = 'Chrome';
    else if (ua.includes('firefox/')) browser = 'Firefox';
    else if (ua.includes('safari/') && !ua.includes('chrome/')) browser = 'Safari';
    
    return `${os} • ${browser}`;
  };

  // Format date
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Format duration
  const formatDuration = (ms) => {
    // Handle invalid inputs
    if (ms === null || ms === undefined || isNaN(ms)) {
      return '0s';
    }
    
    // Take absolute value to handle negative durations
    const absMs = Math.abs(ms);
    
    const seconds = Math.floor(absMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Fetch session details from new API
  const fetchSessionDetails = async (sessionId) => {
    try {
      const url = `${API_ENDPOINT}/session/details`;
      console.log('Fetching session details from:', url, 'with user:', DEFAULT_USER, 'sessionId:', sessionId);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          user: DEFAULT_USER,
          sessionId: sessionId 
        })
      });
      
      if (!response.ok) {
        console.warn(`Session details API failed with ${response.status}: ${response.statusText}. Continuing without enhanced details.`);
        return null;
      }
      
      const data = await response.json();
      console.log('Session details response:', data);
      console.log('Document count:', data.documentCount);
      console.log('Documents array length:', data.documents?.length);
      
      return data;
    } catch (error) {
      console.warn('Error fetching session details (non-critical):', error.message);
      return null;
    }
  };

  // Fetch session events for playback
  const fetchSessionEvents = async (sessionId) => {
    try {
      const url = `${API_ENDPOINT}/session/${DEFAULT_USER}/${sessionId}${selectedDate ? `?date=${selectedDate}` : ''}`;
      console.log('Fetching session events from:', url);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Session events:', data);
      
      return data.events || [];
    } catch (error) {
      console.error('Error fetching session events:', error);
      return null;
    }
  };

  // Load view counts from localStorage
  useEffect(() => {
    const savedViewCounts = localStorage.getItem('sessionViewCounts');
    if (savedViewCounts) {
      setViewCounts(JSON.parse(savedViewCounts));
    }
  }, []);

  // Save view counts to localStorage
  const saveViewCount = (sessionId) => {
    const newViewCounts = {
      ...viewCounts,
      [sessionId]: (viewCounts[sessionId] || 0) + 1
    };
    setViewCounts(newViewCounts);
    localStorage.setItem('sessionViewCounts', JSON.stringify(newViewCounts));
  };

  // Handle session selection and video loading
  const handleSessionClick = async (session) => {
    console.log('========== SESSION CLICKED ==========');
    console.log('Full Session Object:', session);
    console.log('Session ID:', session.sessionId);
    console.log('Event Count:', session.eventCount);
    console.log('Pages Array:', session.pages);
    console.log('Start Time:', session.startTime);
    console.log('Last Update:', session.lastUpdate);
    console.log('Tracking Data:', session.tracking);
    console.log('Page Info:', session.pageInfo);
    console.log('User Info:', session.userInfo);
    console.log('=====================================');
    
    setSelectedSession(session);
    saveViewCount(session.sessionId);
    setSessionEvents(null); // Reset previous session
    setSessionDetails(null); // Reset previous details
    
    // Fetch session details (optional - if API fails, we continue without it)
    const details = await fetchSessionDetails(session.sessionId);
    if (details && details.success) {
      console.log('========== SESSION DETAILS FROM API ==========');
      console.log('Full Details Object:', details);
      console.log('Document Count:', details.documentCount);
      console.log('Documents:', details.documents?.length);
      
      // Extract pages with timestamps from documents
      const pagesWithTimestamp = [];
      if (details.documents && Array.isArray(details.documents)) {
        details.documents.forEach(doc => {
          const pageUrl = doc.data?.pageInfo?.url;
          const pageTitle = doc.data?.pageInfo?.title;
          const timestamp = doc.data?.pageInfo?.timestamp;
          const sessionStartTime = doc.data?.sessionStartTime;
          
          if (pageUrl && timestamp) {
            pagesWithTimestamp.push({
              url: pageUrl,
              title: pageTitle || pageUrl,
              timestamp: timestamp,
              sessionStartTime: sessionStartTime
            });
          }
        });
      }
      
      // Sort by timestamp and get unique pages
      pagesWithTimestamp.sort((a, b) => a.timestamp - b.timestamp);
      
      // Get unique pages (keeping the first occurrence of each URL)
      const uniquePagesMap = new Map();
      pagesWithTimestamp.forEach(page => {
        if (!uniquePagesMap.has(page.url)) {
          uniquePagesMap.set(page.url, page);
        }
      });
      const uniquePages = Array.from(uniquePagesMap.values());
      
      console.log('Unique Pages from API:', uniquePages.length, uniquePages);
      
      // Extract referrer from the earliest document (first in sorted order)
      let sessionReferrer = 'Direct';
      if (details.documents && details.documents.length > 0) {
        // Sort documents by timestamp to find the earliest
        const sortedDocs = [...details.documents].sort((a, b) => {
          const timeA = a.data?.pageInfo?.timestamp || 0;
          const timeB = b.data?.pageInfo?.timestamp || 0;
          return timeA - timeB;
        });
        
        // Get referrer from the earliest document
        const earliestDoc = sortedDocs[0];
        const referrerFromPageInfo = earliestDoc.data?.pageInfo?.referrer;
        const referrerFromTracking = earliestDoc.tracking?.referrer;
        
        // Use pageInfo referrer first, then tracking referrer
        const extractedReferrer = referrerFromPageInfo || referrerFromTracking;
        
        // Only set if it's not "N/A" or empty
        if (extractedReferrer && extractedReferrer !== 'N/A' && extractedReferrer.trim() !== '') {
          sessionReferrer = extractedReferrer;
        }
        
        console.log('Extracted Referrer from earliest document:', sessionReferrer);
      }
      console.log('==============================================');
      
      // Update the session object with pages data and referrer
      const updatedSession = {
        ...session,
        pages: uniquePages,
        apiReferrer: sessionReferrer // Store the referrer from earliest timestamp
      };
      
      // Update the sessions array to include pages data for this session
      setSessions(prevSessions => 
        prevSessions.map(s => 
          s.sessionId === session.sessionId ? updatedSession : s
        )
      );
      
      // Update selected session with pages data
      setSelectedSession(updatedSession);
      
      setSessionDetails(details);
    } else {
      // Set a flag to indicate we tried but failed/skipped session details
      setSessionDetails({ _failed: true });
    }
  };

  // Load video events and initialize player
  const loadVideo = async (sessionId) => {
    setLoadingVideo(true);
    const events = await fetchSessionEvents(sessionId);
    if (events && events.length > 0) {
      setSessionEvents(events);
    }
    setLoadingVideo(false);
  };

  // Initialize rrweb player
  const initializePlayer = useCallback(async () => {
    if (!sessionEvents || !playerRef.current || !selectedSession) return;

    try {
      // Dynamically import rrweb-player
      const rrwebPlayer = (await import('rrweb-player')).default;
      
      // Get consolidated user info from all possible sources including sessionDetails
      const consolidatedUserInfo = getUserInfo(selectedSession, sessionDetails);
      
      // Get device info for sizing
      const deviceInfo = detectDeviceType(consolidatedUserInfo);
      const viewport = consolidatedUserInfo.viewportSize !== 'N/A' ? consolidatedUserInfo.viewportSize : '1024x768';
      const [originalWidth, originalHeight] = viewport.split('x').map(v => parseInt(v));
      
      // Calculate player dimensions to fit in container
      let width = originalWidth;
      let height = originalHeight;
      
      // Max dimensions for the container
      const maxWidth = 800;
      const maxHeight = 600;
      
      // Scale down if needed while maintaining aspect ratio
      const aspectRatio = originalWidth / originalHeight;
      if (width > maxWidth) {
        width = maxWidth;
        height = Math.floor(width / aspectRatio);
      }
      if (height > maxHeight) {
        height = maxHeight;
        width = Math.floor(height * aspectRatio);
      }
      
      // Clear previous player
      if (replayerRef.current) {
        try {
          if (typeof replayerRef.current.destroy === 'function') {
            replayerRef.current.destroy();
          } else if (replayerRef.current.$destroy) {
            replayerRef.current.$destroy();
          }
        } catch (e) {
          console.debug('Error destroying previous player:', e);
        }
      }
      playerRef.current.innerHTML = '';

      // Suppress console warnings from rrweb
      const originalWarn = console.warn;
      console.warn = (...args) => {
        if (args[0]?.includes?.('[replayer]')) return;
        originalWarn.apply(console, args);
      };
      
      replayerRef.current = new rrwebPlayer({
        target: playerRef.current,
        props: {
          events: sessionEvents,
          width: width,
          height: height,
          autoPlay: false,
          showController: true,
          speedOption: [1, 2, 4, 8],
          skipInactive: true,
          showWarning: false,
          mouseTail: {
            duration: 1000,
            lineCap: 'round',
            lineWidth: 3,
            strokeStyle: '#667eea'
          },
          UNSAFE_replayCanvas: true,
          triggerFocus: false,
        },
      });

      console.log('Player initialized with', sessionEvents.length, 'events');
    } catch (err) {
      console.error('Error initializing player:', err);
    }
  }, [sessionEvents, selectedSession]);

  // Initial load
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (selectedSession) {
      loadVideo(selectedSession.sessionId);
    }
  }, [selectedSession]);

  // Initialize player when events are loaded
  useEffect(() => {
    // Initialize player if we have events, regardless of sessionDetails status
    // sessionDetails is optional and only provides enhanced information
    if (sessionEvents) {
      initializePlayer();
    }
    
    // Cleanup
    return () => {
      if (replayerRef.current) {
        try {
          if (typeof replayerRef.current.destroy === 'function') {
            replayerRef.current.destroy();
          } else if (replayerRef.current.$destroy) {
            replayerRef.current.$destroy();
          }
        } catch (e) {
          console.debug('Cleanup error:', e);
        }
      }
    };
  }, [sessionEvents, sessionDetails, initializePlayer]);

  return (
    <div className="w-full min-h-screen bg-gray-50">
      {/* Main Content */}
      <main className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-500" />
              <label htmlFor="date-filter" className="font-semibold text-gray-700">Date:</label>
              <input
                type="date"
                id="date-filter"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              />
            </div>

            <button
              onClick={loadSessions}
              className="flex items-center gap-2 px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-all transform hover:scale-105 shadow-md hover:shadow-lg"
            >
              <RefreshCw className="w-4 h-4" />
              Load Sessions
            </button>
          </div>

          {/* Advanced Filters */}
          {sessions.length > 0 && (
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="w-4 h-4 text-gray-600" />
                <h3 className="font-semibold text-gray-700">Filter Sessions</h3>
                {(filters.referrer !== 'all' || filters.utmSource !== 'all' || filters.utmMedium !== 'all' || filters.utmCampaign !== 'all' || filters.hasGclid !== 'all' || filters.hasFbclid !== 'all' || filters.hasTtclid !== 'all' || filters.selectedPages.length > 0 || filters.pageKeywords.length > 0) && (
                  <button
                    onClick={resetFilters}
                    className="ml-auto flex items-center gap-1 px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all"
                  >
                    <X className="w-3 h-3" />
                    Clear Filters
                  </button>
                )}
              </div>

              {/* First Row - Basic Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Referrer Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Referrer</label>
                  <select
                    value={filters.referrer}
                    onChange={(e) => setFilters(prev => ({ ...prev, referrer: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="all">All Referrers</option>
                    <option value="direct">Direct Traffic</option>
                    {filterOptions.referrers.map(ref => (
                      <option key={ref} value={ref}>{ref.length > 40 ? ref.substring(0, 40) + '...' : ref}</option>
                    ))}
                  </select>
                </div>

                {/* UTM Source Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">UTM Source</label>
                  <select
                    value={filters.utmSource}
                    onChange={(e) => setFilters(prev => ({ ...prev, utmSource: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="all">All Sources</option>
                    {filterOptions.utmSources.map(source => (
                      <option key={source} value={source}>{source}</option>
                    ))}
                  </select>
                </div>

                {/* UTM Medium Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">UTM Medium</label>
                  <select
                    value={filters.utmMedium}
                    onChange={(e) => setFilters(prev => ({ ...prev, utmMedium: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="all">All Mediums</option>
                    {filterOptions.utmMediums.map(medium => (
                      <option key={medium} value={medium}>{medium}</option>
                    ))}
                  </select>
                </div>

                {/* UTM Campaign Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">UTM Campaign</label>
                  <select
                    value={filters.utmCampaign}
                    onChange={(e) => setFilters(prev => ({ ...prev, utmCampaign: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="all">All Campaigns</option>
                    {filterOptions.utmCampaigns.map(campaign => (
                      <option key={campaign} value={campaign}>{campaign.length > 30 ? campaign.substring(0, 30) + '...' : campaign}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Third Row - Ad Click ID Filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                {/* Google Click ID Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Google Ads (gclid)</label>
                  <select
                    value={filters.hasGclid}
                    onChange={(e) => setFilters(prev => ({ ...prev, hasGclid: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="all">All Sessions</option>
                    <option value="yes">With gclid</option>
                    <option value="no">Without gclid</option>
                  </select>
                </div>

                {/* Facebook Click ID Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Facebook Ads (fbclid)</label>
                  <select
                    value={filters.hasFbclid}
                    onChange={(e) => setFilters(prev => ({ ...prev, hasFbclid: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="all">All Sessions</option>
                    <option value="yes">With fbclid</option>
                    <option value="no">Without fbclid</option>
                  </select>
                </div>

                {/* TikTok Click ID Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">TikTok Ads (ttclid)</label>
                  <select
                    value={filters.hasTtclid}
                    onChange={(e) => setFilters(prev => ({ ...prev, hasTtclid: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="all">All Sessions</option>
                    <option value="yes">With ttclid</option>
                    <option value="no">Without ttclid</option>
                  </select>
                </div>
              </div>

              {/* Fourth Row - Page Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">

                {/* Page Selection Filter - Multi-select */}
                <div className="relative">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Pages Visited</label>
                  <button
                    onClick={() => setShowPageSelector(!showPageSelector)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white text-left flex items-center justify-between hover:border-purple-400 transition-all"
                  >
                    <span className="truncate">
                      {filters.selectedPages.length === 0 
                        ? 'All Pages' 
                        : `${filters.selectedPages.length} page${filters.selectedPages.length > 1 ? 's' : ''} selected`}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showPageSelector ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown */}
                  {showPageSelector && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                      {/* Search within pages */}
                      <div className="p-2 border-b sticky top-0 bg-white">
                        <input
                          type="text"
                          value={filters.pageContains}
                          onChange={(e) => setFilters(prev => ({ ...prev, pageContains: e.target.value }))}
                          placeholder="Search pages..."
                          className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      
                      {/* Select All / Deselect All */}
                      <div className="p-2 border-b bg-gray-50 flex gap-2">
                        <button
                          onClick={() => selectAllPages()}
                          className="flex-1 px-2 py-1 text-xs bg-purple-100 hover:bg-purple-200 text-purple-700 rounded transition-all"
                        >
                          Select All
                        </button>
                        <button
                          onClick={() => deselectAllPages()}
                          className="flex-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-all"
                        >
                          Clear All
                        </button>
                      </div>
                      
                      {/* Pages List */}
                      <div className="overflow-y-auto max-h-60">
                        {(() => {
                          const filteredPages = filters.pageContains.trim()
                            ? filterOptions.pages.filter(page => {
                                const searchTerm = filters.pageContains.toLowerCase();
                                return page.url.toLowerCase().includes(searchTerm) || 
                                       page.title.toLowerCase().includes(searchTerm);
                              })
                            : filterOptions.pages;

                          return filteredPages.length > 0 ? (
                            filteredPages.map(page => (
                              <label
                                key={page.url}
                                className="flex items-start gap-2 px-3 py-2 hover:bg-purple-50 cursor-pointer transition-all"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="flex items-center h-5 pt-0.5">
                                  <div className={`w-4 h-4 border-2 rounded flex items-center justify-center transition-all ${
                                    filters.selectedPages.includes(page.url)
                                      ? 'bg-purple-600 border-purple-600'
                                      : 'border-gray-300 bg-white'
                                  }`}>
                                    {filters.selectedPages.includes(page.url) && (
                                      <Check className="w-3 h-3 text-white" />
                                    )}
                                  </div>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={filters.selectedPages.includes(page.url)}
                                  onChange={() => togglePageSelection(page.url)}
                                  className="hidden"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-gray-800 break-words">
                                    {page.title && page.title !== page.url ? page.title : 'Untitled'}
                                  </p>
                                  <p className="text-xs text-gray-500 break-all mt-0.5">
                                    {page.url}
                                  </p>
                                </div>
                              </label>
                            ))
                          ) : (
                            <div className="px-3 py-8 text-center text-xs text-gray-500">
                              {filters.pageContains.trim() 
                                ? 'No pages match your search'
                                : 'No pages available'}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>

                {/* Page Keywords Filter - Multiple keywords (AND logic) */}
                <div className="relative">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Page Contains (Keywords)</label>
                  <button
                    onClick={() => setShowKeywordSelector(!showKeywordSelector)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white text-left flex items-center justify-between hover:border-purple-400 transition-all"
                  >
                    <span className="truncate">
                      {filters.pageKeywords.length === 0 
                        ? 'Add keywords...' 
                        : `${filters.pageKeywords.length} keyword${filters.pageKeywords.length > 1 ? 's' : ''}`}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showKeywordSelector ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown */}
                  {showKeywordSelector && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden" style={{ minWidth: '250px' }}>
                      {/* Add keyword input */}
                      <div className="p-3 border-b bg-gray-50">
                        <label className="block text-xs font-medium text-gray-700 mb-2">Add Keyword (press Enter)</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={keywordInput}
                            onChange={(e) => setKeywordInput(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addKeyword();
                              }
                            }}
                            placeholder="e.g. product, checkout..."
                            className="flex-1 px-3 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              addKeyword();
                            }}
                            className="px-3 py-1.5 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded transition-all"
                          >
                            Add
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">Pages must contain ALL keywords</p>
                      </div>
                      
                      {/* Keywords List */}
                      <div className="max-h-60 overflow-y-auto">
                        {filters.pageKeywords.length > 0 ? (
                          <>
                            <div className="p-2 border-b bg-gray-50 flex justify-between items-center">
                              <span className="text-xs font-medium text-gray-700">Active Keywords:</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  clearAllKeywords();
                                }}
                                className="text-xs text-red-600 hover:text-red-700 font-medium"
                              >
                                Clear All
                              </button>
                            </div>
                            <div className="p-2 space-y-1">
                              {filters.pageKeywords.map((keyword, index) => (
                                <div
                                  key={index}
                                  className="flex items-center justify-between px-3 py-2 bg-purple-50 border border-purple-200 rounded hover:bg-purple-100 transition-all"
                                >
                                  <span className="text-xs font-medium text-purple-900 flex-1 break-all">
                                    {keyword}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeKeyword(keyword);
                                    }}
                                    className="ml-2 p-1 hover:bg-purple-200 rounded transition-all"
                                    title="Remove keyword"
                                  >
                                    <X className="w-3 h-3 text-purple-700" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <div className="px-3 py-8 text-center text-xs text-gray-500">
                            No keywords added yet.<br />
                            Add keywords to filter sessions.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Click outside to close dropdowns */}
              {showPageSelector && (
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowPageSelector(false)}
                />
              )}
              {showKeywordSelector && (
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowKeywordSelector(false)}
                />
              )}

              {/* Active Filters Summary */}
              {(filters.referrer !== 'all' || filters.utmSource !== 'all' || filters.utmMedium !== 'all' || filters.utmCampaign !== 'all' || filters.hasGclid !== 'all' || filters.hasFbclid !== 'all' || filters.hasTtclid !== 'all' || filters.selectedPages.length > 0 || filters.pageKeywords.length > 0) && (
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-600">
                  <span className="font-medium">Active filters:</span>
                  <div className="flex flex-wrap gap-2">
                    {filters.referrer !== 'all' && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full">
                        Referrer: {filters.referrer === 'direct' ? 'Direct' : filters.referrer.substring(0, 20)}
                      </span>
                    )}
                    {filters.utmSource !== 'all' && (
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full">
                        Source: {filters.utmSource}
                      </span>
                    )}
                    {filters.utmMedium !== 'all' && (
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full">
                        Medium: {filters.utmMedium}
                      </span>
                    )}
                    {filters.utmCampaign !== 'all' && (
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full">
                        Campaign: {filters.utmCampaign.substring(0, 20)}
                      </span>
                    )}
                    {filters.hasGclid !== 'all' && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                        Google Ads: {filters.hasGclid === 'yes' ? 'With gclid' : 'No gclid'}
                      </span>
                    )}
                    {filters.hasFbclid !== 'all' && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                        Facebook Ads: {filters.hasFbclid === 'yes' ? 'With fbclid' : 'No fbclid'}
                      </span>
                    )}
                    {filters.hasTtclid !== 'all' && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                        TikTok Ads: {filters.hasTtclid === 'yes' ? 'With ttclid' : 'No ttclid'}
                      </span>
                    )}
                    {filters.selectedPages.length > 0 && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                        Pages: {filters.selectedPages.length} selected
                      </span>
                    )}
                    {filters.pageKeywords.length > 0 && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full flex items-center gap-1">
                        Keywords: {filters.pageKeywords.map((kw, i) => (
                          <span key={i}>
                            {kw}
                            {i < filters.pageKeywords.length - 1 && ' + '}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Video className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Total Sessions</p>
                <p className="text-2xl font-bold text-purple-600">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Empty Sessions</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.emptySessions}</p>
                </div>
              </div>
              {stats.emptySessions > 0 && (
                <button
                  onClick={() => setShowEmptySessions(!showEmptySessions)}
                  className={`w-full px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                    showEmptySessions
                      ? 'bg-orange-600 text-white hover:bg-orange-700'
                      : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                  }`}
                >
                  {showEmptySessions ? 'Hide Empty' : 'Show Empty'}
                </button>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-pink-100 rounded-lg">
                <Activity className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Total Events</p>
                <p className="text-2xl font-bold text-pink-600">{stats.events.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Avg Duration</p>
                <p className="text-2xl font-bold text-blue-600">{stats.duration}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Globe className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Unique Pages</p>
                <p className="text-2xl font-bold text-green-600">{stats.pages}</p>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
            <p className="text-gray-500 font-medium">Loading sessions...</p>
          </div>
        ) : sessions.length === 0 || sessions.filter(s => s.eventCount > 0).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 bg-white rounded-lg">
            <Video className="w-20 h-20 text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No sessions found</h3>
            <p className="text-gray-500 text-center">
              {sessions.length === 0 
                ? "No session recordings available for this date. Start recording to see sessions here."
                : "All sessions for this date have no recorded events. Only sessions with events are displayed."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-6">
            {/* Left Column - Sessions List */}
            <div className="col-span-12 lg:col-span-3 bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-800">
                  Sessions ({getFilteredSessions().length})
                  {getFilteredSessions().length !== sessions.filter(s => showEmptySessions ? true : s.eventCount > 0).length && (
                    <span className="text-sm text-gray-500 font-normal ml-2">
                      of {sessions.filter(s => showEmptySessions ? true : s.eventCount > 0).length}
                    </span>
                  )}
                  {showEmptySessions && (
                    <span className="text-sm text-orange-600 font-normal ml-2">
                      (including empty)
                    </span>
                  )}
                </h2>
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: '75vh' }}>
                {getFilteredSessions().length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 px-4">
                    <Filter className="w-16 h-16 text-gray-300 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">No sessions match filters</h3>
                    <p className="text-gray-500 text-center mb-4">Try adjusting your filters to see more results</p>
                    <button
                      onClick={resetFilters}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-all"
                    >
                      Clear Filters
                    </button>
                  </div>
                ) : (
                  getFilteredSessions().map((session) => {
                  // Calculate duration with safety checks
                  let startTime = session.startTime || session.timestamp || 0;
                  let endTime = session.lastUpdate || session.endTime || startTime;
                  
                  // Convert to numbers if they're strings (ISO date format)
                  if (typeof startTime === 'string') {
                    startTime = new Date(startTime).getTime();
                  }
                  if (typeof endTime === 'string') {
                    endTime = new Date(endTime).getTime();
                  }
                  
                  // Check if timestamps are in seconds (Unix timestamp < year 3000 in seconds)
                  // If timestamp is less than 10 digits, it's likely in seconds, convert to ms
                  const isInSeconds = startTime < 10000000000;
                  const duration = isInSeconds ? Math.abs(endTime - startTime) * 1000 : Math.abs(endTime - startTime);
                  
                  // Use sessionDetails if this is the selected session, otherwise use session data
                  const isSelected = selectedSession?.sessionId === session.sessionId;
                  const detailsToUse = isSelected ? sessionDetails : null;
                  
                  const userInfo = getUserInfo(session, detailsToUse);
                  const deviceInfo = detectDeviceType(userInfo);
                  const DeviceIcon = deviceInfo.Icon;
                  const pages = session.pages?.map(p => p.title).filter(Boolean).join(', ') || 'N/A';
                  const viewCount = viewCounts[session.sessionId] || 0;
                  const pageUrl = session.pageInfo?.url || session.pages?.[0]?.url || 'N/A';
                  const trackingData = getTrackingData(session, detailsToUse);
                  const deviceDescription = getDeviceInfo(userInfo.userAgent);
                  const platform = userInfo.platform;
                  const timezone = userInfo.timezone;
                  
                  // Get referrer from API (earliest timestamp) or fallback to tracking data
                  const referrer = session.apiReferrer || trackingData.referrer;
                  
                  // Calculate unique pages count
                  const uniquePages = session.pages && Array.isArray(session.pages)
                    ? new Set(session.pages.map(p => p.url || p.pageUrl || p.href).filter(Boolean)).size
                    : (session.pageInfo?.url ? 1 : 0);
                  
                  // Check if UTM parameters exist
                  const hasUTM = trackingData.utm_source !== 'N/A' || 
                                 trackingData.utm_medium !== 'N/A' || 
                                 trackingData.utm_campaign !== 'N/A';

                  return (
                    <div
                      key={session.sessionId}
                      onClick={() => handleSessionClick(session)}
                      className={`p-3 border-b border-gray-100 cursor-pointer transition-all hover:bg-purple-50 ${
                        isSelected ? 'bg-purple-50 border-l-4 border-l-purple-600' : ''
                      }`}
                    >
                      {/* Header with Device */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1">
                          <DeviceIcon className="w-4 h-4 text-gray-600" />
                          <span className="text-xs font-semibold text-gray-800">{deviceInfo.name}</span>
                        </div>
                      </div>

                      {/* Compact Info */}
                      <div className="space-y-1.5 text-xs">
                        {/* Events and Pages */}
                        <div className="flex items-center justify-between">
                          <span className={`${session.eventCount === 0 ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                            {session.eventCount} events
                          </span>
                          <span className="text-purple-600 font-medium">
                            {uniquePages} {uniquePages === 1 ? 'page' : 'pages'}
                          </span>
                        </div>

                        {/* Entry and Exit URLs */}
                        {(() => {
                          console.log('Session pages data:', {
                            sessionId: session.sessionId,
                            pages: session.pages,
                            pagesLength: session.pages?.length,
                            hasPages: !!(session.pages && session.pages.length > 0)
                          });
                          
                          if (!session.pages || session.pages.length === 0) {
                            return (
                              <div className="bg-gray-50 p-1.5 rounded">
                                <p className="text-xs text-gray-500 text-center">No page data</p>
                              </div>
                            );
                          }
                          
                          const entryPage = session.pages[0];
                          const exitPage = session.pages[session.pages.length - 1];
                          
                          return (
                            <div className="bg-blue-50 p-1.5 rounded space-y-1.5">
                              {/* Entry Page with Time */}
                              <div className="space-y-0.5">
                                <div className="flex items-start gap-1">
                                  <span className="font-medium text-blue-900 text-[10px] min-w-[35px] flex-shrink-0">Entry:</span>
                                  <p className="text-blue-700 text-[10px] truncate flex-1" title={(entryPage?.url || entryPage?.pageUrl || entryPage?.href) || 'N/A'}>
                                    {entryPage?.title || entryPage?.url || entryPage?.pageUrl || entryPage?.href || 'N/A'}
                                  </p>
                                </div>
                                {(entryPage?.sessionStartTime || entryPage?.timestamp) && (
                                  <p className="text-blue-600 text-[9px] pl-[36px]">
                                    {formatDate(entryPage.sessionStartTime || entryPage.timestamp)}
                                  </p>
                                )}
                              </div>
                              
                              {/* Exit Page with Time */}
                              <div className="space-y-0.5">
                                <div className="flex items-start gap-1">
                                  <span className="font-medium text-blue-900 text-[10px] min-w-[35px] flex-shrink-0">Exit:</span>
                                  <p className="text-blue-700 text-[10px] truncate flex-1" title={(exitPage?.url || exitPage?.pageUrl || exitPage?.href) || 'N/A'}>
                                    {exitPage?.title || exitPage?.url || exitPage?.pageUrl || exitPage?.href || 'N/A'}
                                  </p>
                                </div>
                                {exitPage?.timestamp && (
                                  <p className="text-blue-600 text-[9px] pl-[36px]">
                                    {formatDate(exitPage.timestamp)}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Device Info */}
                        <div className="text-gray-500 truncate" title={deviceDescription}>
                          {deviceDescription}
                        </div>

                        {/* Referrer - Only show if not Direct */}
                        {referrer && referrer !== 'Direct' && (
                          <div className="bg-green-50 p-1.5 rounded">
                            <div className="flex items-start gap-1">
                              <ExternalLink className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                              <p className="text-green-700 truncate flex-1" title={referrer}>
                                {referrer}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* UTM - Compact version */}
                        {hasUTM && (
                          <div className="bg-orange-50 p-1.5 rounded">
                            <div className="flex items-start gap-1">
                              <Tag className="w-3 h-3 text-orange-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0 space-y-0.5">
                                <div className="text-orange-700 truncate" title={`${trackingData.utm_source} / ${trackingData.utm_medium}`}>
                                  {trackingData.utm_source !== 'N/A' && trackingData.utm_source}
                                  {trackingData.utm_medium !== 'N/A' && ` / ${trackingData.utm_medium}`}
                                </div>
                                {session.enhancedTracking && (
                                  <span className="text-[9px] text-orange-600 font-medium">📍 From URL</span>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* View count badge */}
                        {viewCount > 0 && (
                          <div className="flex items-center gap-1 text-purple-600">
                            <Eye className="w-3 h-3" />
                            <span>Viewed {viewCount}x</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                  })
                )}
              </div>
            </div>

            {/* Right Column - Video Player */}
            <div className="col-span-12 lg:col-span-9">
              <div className="bg-white rounded-lg shadow-sm overflow-hidden sticky top-6">
                {!selectedSession ? (
                  <div className="flex flex-col items-center justify-center py-20 px-4">
                    <Video className="w-16 h-16 text-gray-300 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Select a session</h3>
                    <p className="text-gray-500 text-center">Click on any session from the list to view the recording</p>
                  </div>
                ) : (
                  <>
                    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                      <h3 className="text-lg font-semibold text-gray-800">Session Recording</h3>
                    </div>

                    <div className="p-4">
                      {/* Video Player Area */}
                      <div className="relative bg-gray-100 rounded-lg overflow-hidden mb-4 flex items-center justify-center" style={{ minHeight: '65vh' }}>
                        {loadingVideo ? (
                          <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                            <div className="text-center">
                              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                              <p className="text-white">Loading session replay...</p>
                            </div>
                          </div>
                        ) : sessionEvents ? (
                          <div 
                            ref={playerRef} 
                            className="w-full flex items-center justify-center p-4"
                            style={{ minHeight: '60vh' }}
                          />
                        ) : (
                          <div className="text-center p-8">
                            <Video className="w-16 h-16 text-gray-400 mx-auto mb-3" />
                            <p className="text-gray-600">Loading video player...</p>
                          </div>
                        )}
                      </div>

                      {/* Session Info */}
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div className="col-span-2 bg-gray-50 p-3 rounded-lg">
                          <div className="text-sm space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600 font-medium">Session ID:</span>
                              <code className="text-xs font-mono text-purple-600 bg-white px-2 py-1 rounded">
                                {selectedSession.sessionId.split('_')[2] || selectedSession.sessionId}
                              </code>
                            </div>
                            {selectedSession.eventCount === 0 && (
                              <div className="bg-red-50 border border-red-200 rounded p-2">
                                <p className="text-xs font-semibold text-red-700">⚠️ This session has no recorded events</p>
                              </div>
                            )}
                            {(() => {
                              // Use API data if available, otherwise use session data
                              let entryPage = null;
                              let exitPage = null;

                              if (sessionDetails && sessionDetails.documents && !sessionDetails._failed) {
                                // Extract pages from API documents and sort by timestamp
                                const pagesWithTimestamp = [];
                                sessionDetails.documents.forEach(doc => {
                                  const pageUrl = doc.data?.pageInfo?.url;
                                  const pageTitle = doc.data?.pageInfo?.title;
                                  const timestamp = doc.data?.pageInfo?.timestamp;
                                  const sessionStartTime = doc.data?.sessionStartTime;
                                  
                                  if (pageUrl && timestamp) {
                                    pagesWithTimestamp.push({
                                      url: pageUrl,
                                      title: pageTitle,
                                      timestamp: timestamp,
                                      sessionStartTime: sessionStartTime
                                    });
                                  }
                                });

                                // Sort by timestamp to get entry (earliest) and exit (latest)
                                if (pagesWithTimestamp.length > 0) {
                                  pagesWithTimestamp.sort((a, b) => a.timestamp - b.timestamp);
                                  entryPage = pagesWithTimestamp[0];
                                  exitPage = pagesWithTimestamp[pagesWithTimestamp.length - 1];
                                }
                              } else if (selectedSession.pages && selectedSession.pages.length > 0) {
                                // Fallback to session data
                                entryPage = selectedSession.pages[0];
                                exitPage = selectedSession.pages[selectedSession.pages.length - 1];
                              }

                              return (
                                <div className="bg-blue-50 p-1.5 rounded space-y-1.5">
                                  {/* Entry Page with Time */}
                                  <div className="space-y-0.5">
                                    <div className="flex items-start gap-1">
                                      <span className="font-medium text-blue-900 text-[10px] min-w-[35px] flex-shrink-0">Entry:</span>
                                      <p className="text-blue-700 text-[10px] truncate flex-1" title={(entryPage?.url || entryPage?.pageUrl || entryPage?.href) || 'N/A'}>
                                        {entryPage?.title || entryPage?.url || entryPage?.pageUrl || entryPage?.href || 'N/A'}
                                      </p>
                                    </div>
                                    {(entryPage?.sessionStartTime || entryPage?.timestamp) && (
                                      <p className="text-blue-600 text-[9px] pl-[36px]">
                                        {formatDate(entryPage.sessionStartTime || entryPage.timestamp)}
                                      </p>
                                    )}
                                  </div>
                                  
                                  {/* Exit Page with Time */}
                                  <div className="space-y-0.5">
                                    <div className="flex items-start gap-1">
                                      <span className="font-medium text-blue-900 text-[10px] min-w-[35px] flex-shrink-0">Exit:</span>
                                      <p className="text-blue-700 text-[10px] truncate flex-1" title={(exitPage?.url || exitPage?.pageUrl || exitPage?.href) || 'N/A'}>
                                        {exitPage?.title || exitPage?.url || exitPage?.pageUrl || exitPage?.href || 'N/A'}
                                      </p>
                                    </div>
                                    {exitPage?.timestamp && (
                                      <p className="text-blue-600 text-[9px] pl-[36px]">
                                        {formatDate(exitPage.timestamp)}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                        <div className={`flex items-center justify-between text-sm p-3 rounded-lg ${selectedSession.eventCount === 0 ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                          <span className="text-gray-600 font-medium">Events:</span>
                          <span className={`font-semibold ${selectedSession.eventCount === 0 ? 'text-red-700' : 'text-gray-800'}`}>
                            {selectedSession.eventCount}
                            {selectedSession.eventCount === 0 && ' (Empty)'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm bg-gray-50 p-3 rounded-lg">
                          <span className="text-gray-600 font-medium">Pages:</span>
                          <span className="font-semibold text-gray-800">
                            {(() => {
                              // Use API data if available, otherwise fall back to session data
                              if (sessionDetails && sessionDetails.documents && !sessionDetails._failed) {
                                const uniquePages = new Set();
                                sessionDetails.documents.forEach(doc => {
                                  const pageUrl = doc.data?.pageInfo?.url;
                                  if (pageUrl) {
                                    uniquePages.add(pageUrl);
                                  }
                                });
                                return uniquePages.size;
                              }
                              return selectedSession.pages?.length || 0;
                            })()}
                          </span>
                        </div>
                      </div>

                      {/* Device & System Information */}
                      <div className="mb-3">
                        <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-100">
                          <div className="flex items-start gap-2 mb-2">
                            <Monitor className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-indigo-900 mb-2">Device & System</p>
                              <div className="space-y-1.5">
                                {(() => {
                                  const selectedUserInfo = getUserInfo(selectedSession, sessionDetails);
                                  const selectedDeviceInfo = detectDeviceType(selectedUserInfo);
                                  return (
                                    <>
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-indigo-700 font-medium">Device Type:</span>
                                        <span className="text-xs text-indigo-900 font-semibold">
                                          {selectedDeviceInfo.name}
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-indigo-700 font-medium">Platform:</span>
                                        <span className="text-xs text-indigo-900">
                                          {selectedUserInfo.platform}
                                        </span>
                                      </div>
                                      <div className="flex items-start justify-between">
                                        <span className="text-xs text-indigo-700 font-medium">Browser & OS:</span>
                                        <span className="text-xs text-indigo-900 text-right">
                                          {getDeviceInfo(selectedUserInfo.userAgent)}
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-indigo-700 font-medium flex items-center gap-1">
                                          <MapPin className="w-3 h-3" />
                                          Timezone:
                                        </span>
                                        <span className="text-xs text-indigo-900">
                                          {selectedUserInfo.timezone}
                                        </span>
                                      </div>
                                      {selectedUserInfo.language !== 'N/A' && (
                                        <div className="flex items-center justify-between">
                                          <span className="text-xs text-indigo-700 font-medium">Language:</span>
                                          <span className="text-xs text-indigo-900">
                                            {selectedUserInfo.language}
                                          </span>
                                        </div>
                                      )}
                                      <div className="flex items-center justify-between pt-1 border-t border-indigo-200">
                                        <span className="text-xs text-indigo-700 font-medium">Screen:</span>
                                        <span className="text-xs text-indigo-900">
                                          {selectedUserInfo.screenResolution}
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-indigo-700 font-medium">Viewport:</span>
                                        <span className="text-xs text-indigo-900">
                                          {selectedUserInfo.viewportSize}
                                        </span>
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Referrer & Tracking Info */}
                      {(() => {
                        const trackingData = getTrackingData(selectedSession, sessionDetails);
                        const hasUTM = trackingData.utm_source !== 'N/A' || 
                                       trackingData.utm_medium !== 'N/A' || 
                                       trackingData.utm_campaign !== 'N/A';
                        const hasAdIds = trackingData.gclid !== 'N/A' || 
                                        trackingData.fbclid !== 'N/A' || 
                                        trackingData.ttclid !== 'N/A';
                        
                        return (
                          <>
                            {/* Referrer */}
                            {trackingData.referrer && trackingData.referrer !== 'Direct' && (
                              <div className="mb-3">
                                <div className="bg-green-50 p-3 rounded-lg border border-green-100">
                                  <div className="flex items-start gap-2">
                                    <ExternalLink className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-semibold text-green-900 mb-1">Referrer</p>
                                      <p className="text-xs text-green-700 break-all">
                                        {trackingData.referrer}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* UTM Parameters */}
                            {hasUTM && (
                              <div className="mb-3">
                                <div className="bg-orange-50 p-3 rounded-lg border border-orange-100">
                                  <div className="flex items-start gap-2">
                                    <Tag className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between mb-2">
                                        <p className="text-xs font-semibold text-orange-900">UTM Campaign Parameters</p>
                                        {selectedSession.enhancedTracking && (
                                          <span className="text-[10px] bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full font-medium">
                                            📍 Extracted from URL
                                          </span>
                                        )}
                                      </div>
                                      <div className="space-y-1.5">
                                        {trackingData.utm_source !== 'N/A' && (
                                          <div className="flex items-center justify-between">
                                            <span className="text-xs text-orange-700 font-medium">Source:</span>
                                            <span className="text-xs text-orange-900 font-semibold">
                                              {trackingData.utm_source}
                                            </span>
                                          </div>
                                        )}
                                        {trackingData.utm_medium !== 'N/A' && (
                                          <div className="flex items-center justify-between">
                                            <span className="text-xs text-orange-700 font-medium">Medium:</span>
                                            <span className="text-xs text-orange-900 font-semibold">
                                              {trackingData.utm_medium}
                                            </span>
                                          </div>
                                        )}
                                        {trackingData.utm_campaign !== 'N/A' && (
                                          <div className="flex items-start justify-between">
                                            <span className="text-xs text-orange-700 font-medium">Campaign:</span>
                                            <span className="text-xs text-orange-900 text-right font-semibold max-w-[60%] break-words">
                                              {trackingData.utm_campaign}
                                            </span>
                                          </div>
                                        )}
                                        {trackingData.utm_term !== 'N/A' && (
                                          <div className="flex items-start justify-between">
                                            <span className="text-xs text-orange-700 font-medium">Term:</span>
                                            <span className="text-xs text-orange-900 text-right max-w-[60%] break-words">
                                              {trackingData.utm_term}
                                            </span>
                                          </div>
                                        )}
                                        {trackingData.utm_content !== 'N/A' && (
                                          <div className="flex items-start justify-between">
                                            <span className="text-xs text-orange-700 font-medium">Content:</span>
                                            <span className="text-xs text-orange-900 text-right max-w-[60%] break-words">
                                              {trackingData.utm_content}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Ad IDs */}
                            {hasAdIds && (
                              <div className="mb-3">
                                <div className="bg-purple-50 p-3 rounded-lg border border-purple-100">
                                  <div className="flex items-start gap-2">
                                    <Tag className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between mb-2">
                                        <p className="text-xs font-semibold text-purple-900">Ad Click IDs</p>
                                        {selectedSession.enhancedTracking && (
                                          <span className="text-[10px] bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full font-medium">
                                            📍 Extracted from URL
                                          </span>
                                        )}
                                      </div>
                                      <div className="space-y-1.5">
                                        {trackingData.gclid !== 'N/A' && (
                                          <div className="flex items-start justify-between">
                                            <span className="text-xs text-purple-700 font-medium">Google (gclid):</span>
                                            <code className="text-xs text-purple-900 bg-purple-100 px-1.5 py-0.5 rounded font-mono max-w-[60%] break-all">
                                              {trackingData.gclid}
                                            </code>
                                          </div>
                                        )}
                                        {trackingData.fbclid !== 'N/A' && (
                                          <div className="flex items-start justify-between">
                                            <span className="text-xs text-purple-700 font-medium">Facebook (fbclid):</span>
                                            <code className="text-xs text-purple-900 bg-purple-100 px-1.5 py-0.5 rounded font-mono max-w-[60%] break-all">
                                              {trackingData.fbclid}
                                            </code>
                                          </div>
                                        )}
                                        {trackingData.ttclid !== 'N/A' && (
                                          <div className="flex items-start justify-between">
                                            <span className="text-xs text-purple-700 font-medium">TikTok (ttclid):</span>
                                            <code className="text-xs text-purple-900 bg-purple-100 px-1.5 py-0.5 rounded font-mono max-w-[60%] break-all">
                                              {trackingData.ttclid}
                                            </code>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}

                      {/* Entry and Exit URLs */}
                      {(() => {
                        // Use API data if available, otherwise use session data
                        let entryPage = null;
                        let exitPage = null;

                        if (sessionDetails && sessionDetails.documents && !sessionDetails._failed) {
                          // Extract pages from API documents and sort by timestamp
                          const pagesWithTimestamp = [];
                          sessionDetails.documents.forEach(doc => {
                            const pageUrl = doc.data?.pageInfo?.url;
                            const pageTitle = doc.data?.pageInfo?.title;
                            const timestamp = doc.data?.pageInfo?.timestamp;
                            const sessionStartTime = doc.data?.sessionStartTime;
                            
                            if (pageUrl && timestamp) {
                              pagesWithTimestamp.push({
                                url: pageUrl,
                                title: pageTitle,
                                timestamp: timestamp,
                                sessionStartTime: sessionStartTime
                              });
                            }
                          });

                          // Sort by timestamp to get entry (earliest) and exit (latest)
                          if (pagesWithTimestamp.length > 0) {
                            pagesWithTimestamp.sort((a, b) => a.timestamp - b.timestamp);
                            entryPage = pagesWithTimestamp[0];
                            exitPage = pagesWithTimestamp[pagesWithTimestamp.length - 1];
                          }
                        } else if (selectedSession.pages && selectedSession.pages.length > 0) {
                          // Fallback to session data
                          entryPage = selectedSession.pages[0];
                          exitPage = selectedSession.pages[selectedSession.pages.length - 1];
                        }

                        // Only render if we have entry or exit page data
                        if (!entryPage && !exitPage) return null;

                        return (
                          <div className="mb-3">
                            <div className="bg-teal-50 p-3 rounded-lg border border-teal-100">
                              <div className="flex items-start gap-2">
                                <ExternalLink className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-semibold text-teal-900 mb-2">Session Journey</p>
                                  <div className="space-y-2">
                                    {/* Entry URL */}
                                    {entryPage && (
                                      <div className="bg-white p-2 rounded border border-teal-200">
                                        <p className="text-xs font-medium text-teal-800 mb-1">Entry Page</p>
                                        {entryPage.title && (
                                          <p className="text-xs font-medium text-teal-900 mb-1 break-words">
                                            {entryPage.title}
                                          </p>
                                        )}
                                        <p className="text-xs text-teal-700 break-all">
                                          {entryPage.url || entryPage.pageUrl || entryPage.href || 'N/A'}
                                        </p>
                                        {(entryPage.sessionStartTime || entryPage.timestamp) && (
                                          <p className="text-xs text-gray-500 mt-1">
                                            {formatDate(entryPage.sessionStartTime || entryPage.timestamp)}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                    {/* Exit URL */}
                                    {exitPage && (
                                      <div className="bg-white p-2 rounded border border-teal-200">
                                        <p className="text-xs font-medium text-teal-800 mb-1">Exit Page</p>
                                        {exitPage.title && (
                                          <p className="text-xs font-medium text-teal-900 mb-1 break-words">
                                            {exitPage.title}
                                          </p>
                                        )}
                                        <p className="text-xs text-teal-700 break-all">
                                          {exitPage.url || exitPage.pageUrl || exitPage.href || 'N/A'}
                                        </p>
                                        {(exitPage.sessionStartTime || exitPage.timestamp) && (
                                          <p className="text-xs text-gray-500 mt-1">
                                            {formatDate(exitPage.sessionStartTime || exitPage.timestamp)}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Pages Visited */}
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Globe className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-blue-900 mb-2">
                              Pages Visited ({(() => {
                                // Use API data if available
                                if (sessionDetails && sessionDetails.documents && !sessionDetails._failed) {
                                  const uniquePages = new Set();
                                  sessionDetails.documents.forEach(doc => {
                                    const pageUrl = doc.data?.pageInfo?.url;
                                    if (pageUrl) uniquePages.add(pageUrl);
                                  });
                                  return uniquePages.size;
                                }
                                return selectedSession.pages?.length || 0;
                              })()})
                            </p>
                            <div className="space-y-3 max-h-60 overflow-y-auto">
                              {(() => {
                                // Use API data if available, otherwise use session data
                                let pagesToDisplay = [];
                                
                                if (sessionDetails && sessionDetails.documents && !sessionDetails._failed) {
                                  // Extract unique pages from API documents
                                  const pagesMap = new Map();
                                  sessionDetails.documents.forEach(doc => {
                                    const pageUrl = doc.data?.pageInfo?.url;
                                    const pageTitle = doc.data?.pageInfo?.title;
                                    const timestamp = doc.data?.pageInfo?.timestamp;
                                    const sessionStartTime = doc.data?.sessionStartTime;
                                    
                                    if (pageUrl && !pagesMap.has(pageUrl)) {
                                      pagesMap.set(pageUrl, {
                                        url: pageUrl,
                                        title: pageTitle,
                                        timestamp: timestamp,
                                        sessionStartTime: sessionStartTime
                                      });
                                    }
                                  });
                                  pagesToDisplay = Array.from(pagesMap.values());
                                } else if (selectedSession.pages && selectedSession.pages.length > 0) {
                                  pagesToDisplay = selectedSession.pages;
                                }

                                return pagesToDisplay.length > 0 ? (
                                  pagesToDisplay.map((page, index) => (
                                    <div key={index} className="bg-white p-2 rounded border border-blue-200">
                                      <div className="flex items-start gap-2 mb-1">
                                        <span className="text-xs font-semibold text-blue-600 flex-shrink-0">
                                          {index + 1}.
                                        </span>
                                        <div className="flex-1 min-w-0">
                                          {page.title && (
                                            <p className="text-xs font-medium text-blue-900 mb-1 break-words">
                                              {page.title}
                                            </p>
                                          )}
                                          <p className="text-xs text-blue-700 break-all">
                                            {page.url || 'N/A'}
                                          </p>
                                          {page.timestamp && (
                                            <p className="text-xs text-gray-500 mt-1">
                                              {formatDate(page.timestamp)}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-xs text-blue-700">No page data available</p>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SessionReplayDashboard;
